// One user intention = one Idempotency-Key.
//
// A publish or retry request can end in a way the page cannot interpret (dropped connection, 5xx, an empty 200). Sending
// it again as a brand-new request could create a second publication. So the page generates a key when a person starts an
// action, sends it as `Idempotency-Key`, and reuses it ONLY for an uncertain retry of that same intention. The server
// then replays the original result instead of doing the work twice. A deliberate new publication gets a new key.
//
// The unresolved key survives navigation and reload (sessionStorage, memory fallback) and is scoped by action and
// target, for example `publish|<clientId>|<masterId>` or `retry|<jobId>`. A key is a random identifier: it is not a
// credential and carries no secret. It is sent only to the dashboard's own API.
//
// This is a client-side convenience for safe replay. It does not replace server-side enforcement, and it grants no
// permission.

import { ApiError } from "../../api";

const STORE_KEY = "cloudpush_intents_v1";
const TTL_SECONDS = 6 * 60 * 60; // the server keeps keys far longer; an old unresolved intent is not worth resurrecting
const MAX_ENTRIES = 100;
const KEY_RE = /^[A-Za-z0-9._:-]{1,200}$/;

export interface Intent {
  key: string;
  /** Epoch seconds when the intention was created. */
  at: number;
}
type Book = Record<string, Intent>;

let memory: Book = {};
/** Set once a storage call has thrown (blocked storage, quota): from then on the memory copy is the only record. */
let useMemory = false;
let clock: () => number = () => Math.floor(Date.now() / 1000);
/** Tests can pin time. */
export function resetIntentsForTests() {
  memory = {};
  useMemory = false;
}
export function setIntentClock(fn: (() => number) | null) {
  clock = fn ?? (() => Math.floor(Date.now() / 1000));
}

function newKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return `cp-${c.randomUUID()}`;
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return `cp-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function valid(v: unknown): v is Intent {
  return typeof v === "object" && v !== null && typeof (v as Intent).key === "string" && KEY_RE.test((v as Intent).key) && typeof (v as Intent).at === "number" && Number.isFinite((v as Intent).at);
}

function read(): Book {
  if (useMemory) return prune(memory);
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORE_KEY);
  } catch {
    useMemory = true;
    return prune(memory);
  }
  if (raw === null) return {}; // storage works and holds nothing: there is no unresolved intention
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const book: Book = {};
    for (const [scope, v] of Object.entries(parsed as Record<string, unknown>)) if (valid(v)) book[scope] = v;
    return prune(book);
  } catch {
    return {}; // corrupt: start clean rather than reuse something unreadable
  }
}

function prune(book: Book): Book {
  const now = clock();
  const live = Object.entries(book).filter(([, v]) => now - v.at < TTL_SECONDS && v.at <= now + 60);
  live.sort((a, b) => b[1].at - a[1].at);
  return Object.fromEntries(live.slice(0, MAX_ENTRIES));
}

function write(book: Book) {
  const kept = prune(book); // the cap applies to what is stored, including the entry just added
  memory = kept;
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(kept));
  } catch {
    useMemory = true; // storage blocked: the memory copy still covers navigation within this page load
  }
}

export const publishScope = (clientId: string, masterId: string) => `publish|${clientId}|${masterId}`;
export const retryScope = (jobId: string) => `retry|${jobId}`;

/** The unresolved intention for `scope`, if there is one. */
export function peekIntent(scope: string): Intent | null {
  return read()[scope] ?? null;
}

/** Reuses the unresolved intention for `scope` (an uncertain retry of the same action) or starts a new one. */
export function intentFor(scope: string): Intent {
  const book = read();
  const existing = book[scope];
  if (existing) return existing;
  return replaceIntent(scope);
}

/** A deliberate NEW action: always a new key, replacing any unresolved one. */
export function replaceIntent(scope: string): Intent {
  const book = read();
  const intent = { key: newKey(), at: clock() };
  book[scope] = intent;
  write(book);
  return intent;
}

/** The action's result is known (accepted, refused, or replayed): the intention is finished. */
export function resolveIntent(scope: string) {
  const book = read();
  if (scope in book) {
    delete book[scope];
    write(book);
  }
}

/** Drops every unresolved intention whose scope starts with `prefix` (for example all retries once the list shows them settled). */
export function resolveIntentsWhere(match: (scope: string) => boolean) {
  const book = read();
  let changed = false;
  for (const scope of Object.keys(book)) {
    if (match(scope)) {
      delete book[scope];
      changed = true;
    }
  }
  if (changed) write(book);
}

export const IDEMPOTENCY_HEADER = "Idempotency-Key";

/**
 * True when the server definitely did NOT act on the request, so the intention is over and a later click is a new one.
 * Network failures, 5xx and unreadable answers are uncertain: the same key must be reused. "Still being processed" is
 * uncertain too (the other request may yet succeed).
 */
export function isDefinitiveRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === "IDEMPOTENCY_IN_PROGRESS") return false;
  return error.status >= 400 && error.status < 500;
}
